import { lazy, Suspense } from "react";
import ProtectedRoute from "./components/ProtectedRoute";
import LoadingSpinner from "./components/ui/LoadingSpinner";
import { Route, Routes } from "react-router-dom";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { Toaster } from "react-hot-toast";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";
import { ar, en, he } from "./languages";

// Route-level code splitting: guests/landing do not download Event/Admin/Excel chunks.
const Home = lazy(() => import("./Pages/Home"));
const EventPage = lazy(() => import("./Pages/EventPage"));
const LoginPage = lazy(() => import("./Pages/LoginPage"));
const DashboardPage = lazy(() => import("./Pages/DashboardPage"));
const AdminPage = lazy(() => import("./Pages/AdminPage"));
const ContactPage = lazy(() => import("./Pages/ContactPage"));
const PublicRsvpPage = lazy(() => import("./Pages/PublicRsvpPage"));

// Single init — avoids duplicate init on Fast Refresh, which can leave `t()` returning keys.
if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources: {
      en: { translation: en },
      he: { translation: he },
      ar: { translation: ar },
    },
    lng: "ar",
    fallbackLng: ["ar", "en"],
    supportedLngs: ["en", "ar", "he"],
    load: "languageOnly",
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
}

function PageFallback() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: "#363636",
              color: "#fff",
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: "#4ade80",
                secondary: "#fff",
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: "#ef4444",
                secondary: "#fff",
              },
            },
          }}
        />
        <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/events/:eventId/contacts/:contactId"
              element={<ContactPage />}
            />
            <Route path="/events/:eventId/join" element={<PublicRsvpPage />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/events/:eventId"
              element={
                <ProtectedRoute>
                  <EventPage adminMode />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/:eventId"
              element={
                <ProtectedRoute>
                  <EventPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
